import { useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    createNotice,
    publishNotice,
    uploadNoticeAttachments,
    getNoticeEnums,
    getNoticeAudienceOptions,
} from '../../../redux/actions/SocietyPortal/NoticeAction/NoticeAction';
import { getWingsDropdown } from '../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_NOTICES } from '../../../Routing/routes';
import NoticeForm from './NoticeForm';
import { notify } from '../../../utils/notification';

const AddNotice = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const societyId = selectedSocietyStorer.getSocietyId();

    // A ?type=CIRCULAR link still pins the type; without it the form asks which
    // one to create (Notice or Circular).
    const presetType = searchParams.get('type') === 'CIRCULAR' ? 'CIRCULAR' : null;

    const {
        createNoticeLoading,
        noticeEnumsData,
        audienceOptionsData,
        audienceOptionsLoading,
    } = useSelector(state => state.NoticeReducer || {});
    const { wingsDropdown } = useSelector(state => state.WingsAndFlatsReducers || {});
    // One call gives members (with owner/tenant/employee kinds and their flats),
    // flats with occupancy counts, and wings — everything the pickers filter on.
    const members = useMemo(
        () => (Array.isArray(audienceOptionsData?.members) ? audienceOptionsData.members : []),
        [audienceOptionsData]
    );
    const flatOptions = useMemo(
        () => (Array.isArray(audienceOptionsData?.flats) ? audienceOptionsData.flats : []),
        [audienceOptionsData]
    );
    const wings = useMemo(
        () => (Array.isArray(wingsDropdown) ? wingsDropdown : []),
        [wingsDropdown]
    );

    useEffect(() => {
        if (!noticeEnumsData) dispatch(getNoticeEnums());
    }, [dispatch, noticeEnumsData]);
    useEffect(() => {
        if (societyId) dispatch(getWingsDropdown({ society_id: societyId }));
    }, [dispatch, societyId]);
    useEffect(() => {
        if (societyId) dispatch(getNoticeAudienceOptions({ societyId }));
    }, [dispatch, societyId]);

    const backToList = () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_NOTICES);

    const handleSubmit = (payload, files = [], publish = false) => {
        dispatch(createNotice({
            society_id: societyId,
            ...payload,
            onSuccess: (res) => {
                const noticeId = res?.data?.notice_id;
                // Attachments need the record's id, so they go up once the POST
                // lands. Fire-and-forget keeps the user off a spinner while
                // large PDFs upload — the detail view renders them on arrival.
                if (noticeId && files.length > 0) {
                    dispatch(uploadNoticeAttachments({ id: noticeId, files }));
                }
                // "Save & Publish" is a create followed by a publish so the
                // attachment upload above is already in flight when the notice
                // goes live. A future publish_at makes the BE schedule instead.
                if (noticeId && publish) {
                    dispatch(publishNotice({
                        id: noticeId,
                        publish_at: payload.publish_at,
                        onSuccess: backToList,
                    }));
                    return;
                }
                // A new notice is created as a DRAFT and drafts are not on the
                // notice board. Say so, or "saved" reads as "published".
                notify('info', 'Saved as draft. Use Publish to put it on the notice board.');
                backToList();
            },
        }));
    };

    const title = presetType === 'CIRCULAR' ? 'New Circular' : 'New Notice';
    const subtitle = presetType === 'CIRCULAR'
        ? 'Issue a formal circular to society members'
        : 'Publish an announcement to residents';

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            <div className="tw-max-w-4xl tw-mx-auto">
                <button
                    onClick={backToList}
                    className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600 hover:tw-text-gray-800 tw-mb-3"
                >
                    <Icon icon="mdi:arrow-left" /> Back to Notices
                </button>
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm">
                    <div className="tw-p-4 tw-border-b tw-border-gray-100">
                        <h1 className="tw-text-base tw-font-bold tw-text-gray-800">{title}</h1>
                        <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">{subtitle}</p>
                    </div>
                    <div className="tw-p-5">
                        <NoticeForm
                            enums={noticeEnumsData}
                            wings={wings}
                            members={members}
                            membersLoading={!!audienceOptionsLoading}
                            flatOptions={flatOptions}
                            onSubmit={handleSubmit}
                            onCancel={backToList}
                            submitting={createNoticeLoading}
                            mode="create"
                            lockedType={presetType}
                            enableAttachments
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddNotice;
